import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  HomeIcon,
  BookOpenIcon,
  BeakerIcon,
  RocketLaunchIcon,
  DocumentTextIcon,
  TrophyIcon,
  AcademicCapIcon,
  BookmarkIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChartPieIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useProgressStore } from '../stores/progressStore'
import { useAuthStore } from '../stores/authStore'
import { canAccessCertificates, canAccessPremiumResources } from '../utils/tierPermissions'
import { useEffect } from 'react'

const baseNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Modules', href: '/modules', icon: BookOpenIcon },
  { name: 'Labs', href: '/labs', icon: BeakerIcon },
  { name: 'Capstones', href: '/capstones', icon: RocketLaunchIcon },
  { name: 'Resources', href: '/resources', icon: DocumentTextIcon },
  { name: 'Premium Templates', href: '/premium-templates', icon: SparklesIcon, premium: true },
  { name: 'Analytics', href: '/analytics', icon: ChartPieIcon },
  { name: 'My Bookmarks', href: '/bookmarks', icon: BookmarkIcon },
  { name: 'Badges', href: '/badges', icon: TrophyIcon },
  { name: 'Leaderboard', href: '/leaderboard', icon: ChartBarIcon },
  { name: 'Discussions', href: '/discussions', icon: ChatBubbleLeftRightIcon },
]

const adminNavigation = [
  { name: 'Admin Panel', href: '/admin', icon: Cog6ToothIcon, adminOnly: true },
]

const instructorNavigation = [
  { name: 'Instructor Dashboard', href: '/instructor', icon: AcademicCapIcon, instructorOnly: true },
]

export default function Sidebar({ isOpen, onClose }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { progress, fetchProgress, getCompletionPercentage } = useProgressStore()
  const { user } = useAuthStore()
  const hasCertificateAccess = canAccessCertificates(user?.enrolled_tier)
  const hasPremiumAccess = canAccessPremiumResources(user?.enrolled_tier)
  const isInstructor = user?.role === 'instructor'


  useEffect(() => {
    fetchProgress()
  }, [fetchProgress])

  const completionPercentage = getCompletionPercentage()

  // Build navigation array based on user permissions
  let navigation = [...baseNavigation]
  
  // Filter out Premium Templates if user doesn't have premium access (unless admin or instructor)
  if (!hasPremiumAccess && !user?.is_admin && !isInstructor) {
    navigation = navigation.filter(item => !item.premium)
  }
  
  // Add certificates link only if user has access (Complete tier+)
  if (hasCertificateAccess) {
    navigation.push({ name: 'My Certificates', href: '/my-certificates', icon: AcademicCapIcon })
  }

  // Combine navigation items based on role
  let allNavigation = navigation
  
  if (isInstructor) {
    // Instructors get instructor dashboard (role takes priority over admin flag)
    allNavigation = [...navigation, ...instructorNavigation]
  } else if (user?.is_admin) {
    // Pure admins (no instructor role) get admin panel
    allNavigation = [...navigation, ...adminNavigation]
  }

  // Auto-close sidebar on mobile when navigating
  const handleNavClick = (href) => {
    if (window.innerWidth < 1024) {
      onClose?.()
    }
  }

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-30 bg-white border-r border-gray-200 w-64 transform transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } top-14 sm:top-16 overflow-y-auto`}
    >
      <div className="p-4 sm:p-6">
        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden absolute top-4 right-4 p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          aria-label="Close menu"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Branding */}
        <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <img 
              src="/branding/cte-course-platform-icon.svg" 
              alt="CTE Course Platform" 
              className="h-8 w-8 sm:h-10 sm:w-10"
            />
            <div>
              <div className="text-xs sm:text-sm font-bold bg-gradient-to-r from-cte-blue to-cte-blue-dark bg-clip-text text-transparent">
                CTE Course Platform
              </div>
              <div className="text-xs text-gray-500">Your Learning Journey</div>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="mb-4 sm:mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs sm:text-sm font-medium text-gray-700">Course Progress</span>
            <span className="text-xs sm:text-sm font-bold text-cte-blue">{completionPercentage}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Navigation Links */}
        <nav className="space-y-1">
          {allNavigation.map((item) => {
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/')
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleNavClick(item.href)}
                className={`flex items-center px-3 py-2.5 sm:py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-cte-blue text-white'
                    : item.adminOnly 
                      ? 'text-purple-700 hover:bg-purple-50 border border-purple-200'
                      : item.premium
                        ? 'text-indigo-700 hover:bg-indigo-50'
                        : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <item.icon className="h-5 w-5 mr-3 flex-shrink-0" />
                <span className="truncate">{item.name}</span>
                {item.premium && <SparklesIcon className="h-3 w-3 ml-auto text-indigo-500 flex-shrink-0" />}
              </Link>
            )
          })}
        </nav>

        {/* Stats */}
        {progress && (
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
              Your Stats
            </h3>
            <div className="space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Modules</span>
                <span className="font-medium">
                  {progress.completed_modules || 0}/{progress.total_modules || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Labs</span>
                <span className="font-medium">
                  {progress.completed_labs || 0}/{progress.total_labs || 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Capstones</span>
                <span className="font-medium">
                  {progress.completed_capstones || 0}/{progress.total_capstones || 0}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
  )
}
